/**
 * `next/script` — appends a <script> to the document.
 *
 * Next's loading strategies map onto what a browser can actually be told: `afterInteractive`
 * (the default) and `lazyOnload` become `defer` and `async` respectively; `beforeInteractive`
 * cannot be honoured at all from inside a React render, because by the time this component
 * mounts the page is already interactive. A script that genuinely must run first belongs in
 * public/index.html — that is where the activity tracker went.
 */

import { useEffect } from 'react';

interface ScriptProps {
  src?: string;
  id?: string;
  strategy?: 'beforeInteractive' | 'afterInteractive' | 'lazyOnload' | 'worker';
  onLoad?: () => void;
  onError?: (e: unknown) => void;
  children?: string;
  [key: string]: unknown;
}

export default function Script({
  src,
  id,
  strategy = 'afterInteractive',
  onLoad,
  onError,
  children,
  ...rest
}: ScriptProps) {
  useEffect(() => {
    // An id that is already present means this script has been mounted before —
    // remounting the component must not run it twice.
    if (id && document.getElementById(id)) {
      onLoad?.();
      return;
    }

    const element = document.createElement('script');
    if (id) element.id = id;
    if (src) element.src = src;
    if (children) element.textContent = children;

    if (strategy === 'lazyOnload') element.async = true;
    else element.defer = true;

    for (const [key, value] of Object.entries(rest)) {
      if (typeof value === 'string') element.setAttribute(key, value);
    }

    if (onLoad) element.addEventListener('load', onLoad);
    if (onError) element.addEventListener('error', onError);

    document.body.appendChild(element);

    return () => {
      if (onLoad) element.removeEventListener('load', onLoad);
      if (onError) element.removeEventListener('error', onError);
      // Left in the document deliberately. Removing a <script> does not undo what it
      // ran, and the ported call sites mount once for the lifetime of the page.
    };
  }, [src, id, strategy, children]);

  return null;
}
